<template>
    <b-modal
        :id="modalId"
        title="프로젝트 코드 검색"
        size="xl"
        hide-footer
        @shown="onShown"
        @hidden="resetModal"
    >
        <b-container fluid>
            <!-- 검색 + 새로고침 -->
            <b-row class="mb-3 align-items-center">
                <b-col cols="12" md="8" lg="9" class="mb-2 mb-md-0">
                    <b-form-input
                        id="project-code-lookup-filter"
                        v-model="filterKeyword"
                        placeholder="프로젝트 코드 / 회사명 / 캠페인 / 매니저로 검색"
                        size="sm"
                        trim
                        :disabled="loading"
                    />
                </b-col>
                <b-col cols="12" md="4" lg="3" class="text-md-right">
                    <b-button size="sm" variant="outline-secondary" :disabled="loading" @click="refresh">
                        <fa icon="sync-alt" class="mr-1"/>새로고침
                    </b-button>
                </b-col>
            </b-row>

            <!-- 결과 카운트 -->
            <p class="text-secondary small mb-2">
                총 <strong>{{ filteredProjects.length }}</strong>건
                <span v-if="filterKeyword">(전체 {{ projects.length }}건 중)</span>
            </p>

            <!-- 결과 테이블 — 행을 누르면(키보드 Enter) 고른다.
                 DS 표 규칙: 행 44 고정 · 칸마다 한 줄(넘치면 줄이고 전체는 title) -->
            <b-table
                class="mb-0"
                table-class="ds-table ds-table-fixed project-code-lookup-table"
                hover
                responsive
                sticky-header="450px"
                show-empty
                :items="filteredProjects"
                :fields="fields"
                :busy="loading"
                :per-page="pagination.perPage"
                :current-page="pagination.currentPage"
                :empty-html="emptyTableHTML"
                @row-clicked="onRowClicked"
            >
                <template #table-busy>
                    <div class="d-flex align-items-center justify-content-center my-3">
                        <b-spinner class="mr-2" variant="primary"/>
                        <span>로딩중...</span>
                    </div>
                </template>
                <template #cell(project_code)="{ value }">
                    <strong class="ds-ellipsis" :title="value">{{ value }}</strong>
                </template>
                <template #cell(company_name)="{ value }">
                    <span class="ds-ellipsis" :title="value">{{ value || '-' }}</span>
                </template>
                <template #cell(campaign_name)="{ value }">
                    <span class="ds-ellipsis" :title="value">{{ value || '-' }}</span>
                </template>
                <template #cell(manager)="{ value }">
                    <span class="ds-ellipsis" :title="value">{{ value || '-' }}</span>
                </template>
                <template #cell(period)="{ value }">
                    <span class="ds-ellipsis" :title="value">{{ value || '-' }}</span>
                </template>
                <!-- 행 전체가 고르는 자리라 화살표는 표시만(클릭은 행으로 넘어가고, 키보드는 행이 받는다) -->
                <template #cell(pick)>
                    <span class="ds-row-icon-btn" aria-hidden="true"><AppIcon name="chevron-right"/></span>
                </template>
            </b-table>

            <!-- 페이지네이션 -->
            <div v-if="filteredProjects.length > pagination.perPage" class="d-flex justify-content-center mt-3">
                <b-pagination
                    v-model="pagination.currentPage"
                    :total-rows="filteredProjects.length"
                    :per-page="pagination.perPage"
                    size="sm"
                />
            </div>
        </b-container>
    </b-modal>
</template>

<script>
import { getDefaultPagination, getEmptyTableHTML } from '@shared/components/admin/pages/Management/common-data';

export default {
    name: 'ProjectCodeLookupModal',
    props: {
        modalId: {
            type: String,
            default: 'project-code-lookup-modal',
        },
    },
    data() {
        return {
            loading: false,
            loaded: false,
            loadError: false,
            projects: [],
            filterKeyword: '',
            pagination: getDefaultPagination(1, 20, 0),
            // 폭을 안 적은 회사명 · 캠페인이 남는 폭을 나눠 갖는다(표 최소 폭은 아래 style)
            fields: [
                { key: 'project_code', label: '프로젝트 코드', sortable: true, thStyle: { width: '180px' } },
                { key: 'company_name', label: '회사명', sortable: true },
                { key: 'campaign_name', label: '캠페인', sortable: true },
                { key: 'manager', label: '매니저', sortable: true, thStyle: { width: '120px' } },
                { key: 'period', label: '기간', sortable: true, thStyle: { width: '100px' } },
                // 아이콘 28 + 좌우 16
                { key: 'pick', label: '', thStyle: { width: '60px' }, tdClass: ['text-right'] },
            ],
        };
    },
    computed: {
        emptyTableHTML() {
            if (this.loadError) {
                return getEmptyTableHTML('프로젝트 코드를 불러오지 못했습니다. 새로고침을 눌러주세요.');
            }
            return getEmptyTableHTML(this.loaded ? '조회된 프로젝트가 없습니다.' : '로딩중...');
        },
        filteredProjects() {
            const keyword = this.filterKeyword?.trim().toLowerCase();
            if (!keyword) return this.projects;

            return this.projects.filter(p => {
                const haystack = [
                    p.project_code,
                    p.company_name,
                    p.campaign_name,
                    p.manager,
                    p.period,
                ]
                    .filter(v => v != null && v !== '')
                    .map(v => String(v).toLowerCase())
                    .join(' ');
                return haystack.includes(keyword);
            });
        },
    },
    watch: {
        filterKeyword() {
            this.pagination.currentPage = 1;
        },
    },
    methods: {
        onShown() {
            if (!this.loaded) {
                this.fetchProjects();
            }
        },
        async fetchProjects() {
            this.loading = true;
            this.loadError = false;
            try {
                const { data } = await this.$axios.get('/project_code');
                this.projects = this.flattenResponse(data);
                this.loaded = true;
            } catch (e) {
                console.error('[ProjectCodeLookupModal] fetch failed', e);
                this.$bvToast.toast('프로젝트 코드 목록을 불러오지 못했습니다.', {
                    title: '오류',
                    variant: 'danger',
                });
                this.loadError = true;
                // refresh 실패 시 이전 캐시는 그대로 보존한다.
            } finally {
                this.loading = false;
            }
        },
        flattenResponse(grouped) {
            if (!Array.isArray(grouped)) return [];
            const flat = [];
            grouped.forEach(group => {
                const periodLabel = Array.isArray(group.period) ? group.period[0] : group.period;
                (group.projects || []).forEach(p => {
                    flat.push({
                        ...p,
                        campaign_name: p.campaign ? p.campaign.name : '',
                        period: periodLabel || p.period || '',
                    });
                });
            });
            // 최신순(id 내림차순) 정렬 — id가 큰(최근 생성된) 프로젝트가 먼저 오도록 한다.
            flat.sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
            return flat;
        },
        refresh() {
            // loaded는 그대로 두어 실패해도 이전 캐시를 잃지 않는다.
            // 성공 시 fetchProjects 내부에서 갱신된다.
            this.fetchProjects();
        },
        onRowClicked(item) {
            this.$emit('selected', { project_code: item.project_code, project: item });
            this.$bvModal.hide(this.modalId);
        },
        resetModal() {
            this.filterKeyword = '';
            this.pagination.currentPage = 1;
            this.loadError = false;
        },
    },
};
</script>

<style scoped>
/* 고정 칸 합 460(코드 180 · 매니저 120 · 기간 100 · 화살표 60) + 회사명 · 캠페인 120씩.
   창 본문 좌우 여백은 2rem(30, custom/modal_toasts.scss) — xl 창 본문(1140 - 테두리 2 - 여백 60 - container 30 = 약 1048) 안이고,
   화면 1200 미만에서 800 창이 될 때(약 708)도 가로 스크롤 없이 들어간다 */
::v-deep .project-code-lookup-table {
    min-width: 700px;
}

::v-deep .project-code-lookup-table tbody tr {
    cursor: pointer;
}
</style>
